import React, { useState } from 'react';
import { CornerDownRight, CheckCircle2, Circle, Pencil } from 'lucide-react';
import { Clarification } from '@/types/clarification';
import { Button } from '@/components/ui/button';
import { formatClarificationDate, formatUserName } from './utils';
import ClarificationCommentForm from './ClarificationCommentForm';
import InlineEditForm from './InlineEditForm';
import EditableReply from './EditableReply';
import { useUser } from '@/providers/UserProvider';

interface ClarificationItemProps {
  clarification: Clarification;
  onReply: () => void;
  onReplySubmit: (content: string) => Promise<void>;
  onReplyCancel: () => void;
  isReplying: boolean;
  onResolve: (id: string) => Promise<void>;
  onUnresolve: (id: string) => Promise<void>;
  onUpdate: (id: string, content: string, messageId?: string) => Promise<void>;
  canAddClarifications: boolean;
  canResolveClarifications: boolean;
  canEditClarifications: boolean;
  canReplyOnly: boolean;
}

const ClarificationItem: React.FC<ClarificationItemProps> = ({
  clarification,
  onReply,
  onReplySubmit,
  onReplyCancel,
  isReplying,
  onResolve,
  onUnresolve,
  onUpdate,
  canAddClarifications,
  canResolveClarifications,
  canEditClarifications,
  canReplyOnly,
}) => {
  const { userData } = useUser();
  const currentUserId = userData?.id;
  const [isResolving, setIsResolving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const isUnresolved = !clarification.resolved;

  const canEdit =
    canEditClarifications &&
    clarification.user?.id === currentUserId &&
    isUnresolved;

  const handleResolve = async () => {
    setIsResolving(true);
    try {
      if (clarification.resolved) {
        await onUnresolve(clarification.id);
      } else {
        await onResolve(clarification.id);
      }
    } catch (err) {
      console.error('Failed to toggle resolve status:', err);
    } finally {
      setIsResolving(false);
    }
  };

  return (
    <div
      className={`space-y-2 rounded-lg p-3 ${isUnresolved ? 'border border-amber-400 border-l-4' : 'border border-gray-200'}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1">
          {/* Header: name, date, status badge */}
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-sm font-medium text-gray-900">
              {formatUserName(clarification.user, currentUserId)}
            </span>
            <span className="text-xs text-gray-500">
              {formatClarificationDate(clarification.created_at)}
            </span>
            {clarification.resolved && (
              <span className="text-xs text-green-700 px-2 py-0.5 rounded flex items-center gap-1">
                Resolved
                {clarification.resolved_at && (
                  <span className="text-green-600">
                    {formatClarificationDate(clarification.resolved_at)}
                  </span>
                )}
              </span>
            )}
          </div>

          {/* Content */}
          {isEditing ? (
            <InlineEditForm
              initialContent={clarification.content}
              onSave={async content => {
                await onUpdate(clarification.id, content);
                setIsEditing(false);
              }}
              onCancel={() => setIsEditing(false)}
            />
          ) : (
            <p
              className={`text-sm whitespace-pre-wrap ${clarification.resolved ? 'text-gray-600' : 'text-gray-900'}`}
            >
              {clarification.content}
            </p>
          )}

          {/* Actions */}
          {!isEditing && (canEdit || canResolveClarifications) && (
            <div className="flex items-center gap-2 mt-2">
              {canEdit && (
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                >
                  <Pencil className="h-3 w-3" />
                  Edit
                </button>
              )}
              {canResolveClarifications && (
                <Button
                  onClick={handleResolve}
                  disabled={isResolving}
                  size="sm"
                  variant={clarification.resolved ? 'outline' : 'default'}
                  className={`text-xs flex items-center gap-1 ${
                    clarification.resolved
                      ? ''
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {clarification.resolved ? (
                    <>
                      <Circle className="h-3 w-3" />
                      Unresolve
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-3 w-3" />
                      Resolve
                    </>
                  )}
                </Button>
              )}
            </div>
          )}

          {/* Resolved-by attribution */}
          {clarification.resolved && clarification.resolved_by && (
            <p className="text-xs text-gray-500 mt-1">
              Resolved by{' '}
              {formatUserName(clarification.resolved_by, currentUserId)}
            </p>
          )}
        </div>
      </div>

      {/* Replies */}
      {clarification.replies && clarification.replies.length > 0 && (
        <div className="mt-2 space-y-2">
          {clarification.replies.map(reply =>
            reply.event_type === 'answer_edited' ? (
              <div
                key={reply.id}
                className="flex items-center gap-2 py-2 px-3 bg-gray-50 rounded text-xs text-gray-400"
              >
                <span>
                  <span className="text-xs">
                    {formatUserName(reply.user, currentUserId)}
                  </span>
                  {' edited the answer · '}
                  <span>{formatClarificationDate(reply.created_at)}</span>
                </span>
              </div>
            ) : (
              <EditableReply
                key={reply.id}
                reply={reply}
                clarificationId={clarification.id}
                currentUserId={currentUserId}
                canEdit={
                  canEditClarifications &&
                  reply.user?.id === currentUserId &&
                  isUnresolved
                }
                onUpdate={onUpdate}
              />
            )
          )}
        </div>
      )}

      {/* Inline reply form, or the Reply button — always at the bottom of the thread */}
      {isReplying ? (
        <div className="ml-8 mt-2">
          <ClarificationCommentForm
            onSubmit={onReplySubmit}
            submitLabel="Reply"
            placeholder="Write a reply..."
            onCancel={onReplyCancel}
          />
        </div>
      ) : (
        (canAddClarifications || canReplyOnly) && (
          <div>
            <button
              type="button"
              onClick={onReply}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 px-2 py-1 rounded hover:bg-blue-50 transition-colors"
            >
              <CornerDownRight className="h-3 w-3" />
              Reply
            </button>
          </div>
        )
      )}
    </div>
  );
};

export default ClarificationItem;
